import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { TranslateService } from '@ngx-translate/core';
import { animationFrameScheduler, auditTime, BehaviorSubject, Observable, Subject } from 'rxjs';
import {
  attributeAvailibilityReasonEnum,
  IAttributeValueAvailibility,
} from 'src/app/models/interfaces/GenericInterfaces';
import { Attribute, NumericChange } from 'src/app/models/product-models/views/Attribute';
import { LocalizedText } from 'src/app/models/product-models/views/LocalizedText';
import { Product } from 'src/app/models/product-models/views/Product';
import { UnitConversionDirective } from 'src/app/shared/directives/unit-conversion.directive';
export enum NumericAttributeModeEnum {
  Slider = 'Slider',
  Number = 'Number',
  Select = 'Select',
}

export const NUMERIC_ATTRIBUTE_MODE = 'numericAttributeMode';

@Component({
  selector: 'app-numeric-attribute',
  templateUrl: './numeric-attribute.component.html',
  styleUrls: ['./numeric-attribute.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NumericAttributeComponent implements OnInit, AfterViewInit, OnChanges {
  @Input({ required: true }) attribute!: Attribute;
  @Input() currentValue?: number;

  @Input() showConfirmButton = false;
  @Input() showLabel = false;

  @Input() product?: Product;

  //@Input() mode = NumericAttributeModeEnum.Slider;

  @Output() valueChanged: EventEmitter<number> = new EventEmitter<number>();

  @ViewChild('myDirective') unitConversionDirective?: UnitConversionDirective;
  @ViewChild('rangeInput') rangeInput?: ElementRef<HTMLInputElement>;

  public isInputChanged = new BehaviorSubject(false);

  public possibleValues: number[] = [];

  private _mode: BehaviorSubject<NumericAttributeModeEnum[]> = new BehaviorSubject<NumericAttributeModeEnum[]>([
    NumericAttributeModeEnum.Number,
  ]);

  public mode$: Observable<NumericAttributeModeEnum[]> = this._mode.asObservable();

  private _confirm$ = new Subject<void>();

  constructor(private translateService: TranslateService) {}

  ngOnInit(): void {
    //Called after the constructor, initializing input properties, and the first call to ngOnChanges.
    //Add 'implements OnInit' to the class.
    if (this.attribute.builderData[NUMERIC_ATTRIBUTE_MODE] !== undefined) {
      this._mode.next(this.attribute.builderData[NUMERIC_ATTRIBUTE_MODE]);
    }
    if (this.attribute instanceof NumericChange)
      if (this.attribute) this.possibleValues = this._calculatePossibleValues(this.attribute);

    this._confirm$.pipe(auditTime(0, animationFrameScheduler)).subscribe(() => this.confirmInput());
  }

  ngAfterViewInit(): void {
    //Called after ngAfterContentInit when the component's view has been initialized. Applies to components only.
    //Add 'implements AfterViewInit' to the class.
    this.updateSlider();
  }

  onInputChange(change?: MatSelectChange) {
    if (change !== undefined && typeof change.value === 'number') {
      this.valueChanged.emit(change.value);
      this.current = change.value;
    } else {
      if (!this.showConfirmButton) {
        this.confirmInput();
      }
    }
  }

  public checkIfValueCanBeSet(value: number): IAttributeValueAvailibility {
    if (this.attribute && this.product) return this.product.checkIfAttributeCanBeSet(this.attribute, value);

    return { isAvailible: false, reason: attributeAvailibilityReasonEnum.internalError };
  }

  public parseReason(availibility: IAttributeValueAvailibility) {
    if (!availibility.isAvailible && availibility.reason) {
      return this.translateService.instant('translations.' + availibility.reason);
    }
    return '';
  }

  public numericAttributeModeEnum = NumericAttributeModeEnum;

  public get unit() {
    if (this.attribute instanceof NumericChange) return this.attribute?.unit ? this.attribute?.unit : 'm';
    return 'm';
  }

  public get unitFactor() {
    if (this.attribute instanceof NumericChange) return this.attribute?.unitFactor ? this.attribute?.unitFactor : 1;
    return 1;
  }

  public get numericAttribute() {
    return this.attribute as NumericChange;
  }

  public get current() {
    if (this.currentValue) return this.currentValue;
    return 0;
  }

  public set current(val: number) {
    this.currentValue = val;
  }

  onInput(confirmInput = false) {
    if (this.showConfirmButton) {
      this.isInputChanged.next(true);
    }

    const shouldConfirm = confirmInput === true || this.showConfirmButton === false;
    if (shouldConfirm) {
      this._confirm$.next();
    }
  }

  public confirmInput() {
    this.unitConversionDirective?.checkValidity();
    if (this.currentValue === undefined) return;
    const value = this.currentValue;
    this.valueChanged.emit(value);
    this.isInputChanged.next(false);
  }

  getLocalizedString(texts: LocalizedText[]): string | null {
    if (!texts) return null;
    if (typeof texts === 'string') return texts;

    const text = texts.find((text) => text.language === this.translateService.currentLang);
    return text ? text.text : null;
  }

  private _calculatePossibleValues(attribute: NumericChange) {
    const possibleValues: number[] = [];
    for (let value = attribute.min; value <= attribute.max; value = parseFloat((value + attribute.step).toFixed(2))) {
      possibleValues.push(value);
    }
    return possibleValues;
  }

  updateSlider() {
    const val = this.currentValue;

    const max = (this.attribute as NumericChange).max;
    const min = (this.attribute as NumericChange).min;
    if (val === undefined || min === undefined || max === undefined) return;
    const percent = ((+val - +min) / (+max - +min)) * 100;
    this.rangeInput?.nativeElement.style.setProperty('--range-progress', percent + '%');
  }

  ngOnChanges(changes: SimpleChanges): void {
    //Called before any other lifecycle hook. Use it to inject dependencies, but avoid any serious work here.
    //Add '${implements OnChanges}' to the class.
    this.updateSlider();
  }
}
